// src/helpers/emailMarketingService.js
import { get, post, patch, del } from './apiHelper';

const BASE_URL = 'email-marketing';

const emailMarketingService = {
    // ===== CONTACTOS =====
    
    /**
     * Listar contactos
     * @param {string} marcaId - ID de la marca
     * @param {object} filters - Filtros opcionales (page, limit, acceptsMarketing, tags, rfmSegments, etc.)
     * @returns {Promise<object>}
     */
    getContacts: async (marcaId, filters = {}) => {
        const params = new URLSearchParams({ marcaId });
        
        // Añadir filtros al query string
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                if (Array.isArray(value)) {
                    // Para arrays, añadir cada elemento
                    value.forEach(item => params.append(`${key}[]`, item));
                } else {
                    params.append(key, value);
                }
            }
        });
        
        const url = `${BASE_URL}/contacts?${params.toString()}`;
        return await get(url);
    },
    
    /**
     * Obtener contacto individual
     * @param {string} id - ID del contacto
     * @returns {Promise<object>}
     */
    getContact: async (id) => {
        return await get(`${BASE_URL}/contacts/${id}`);
    },
    
    /**
     * Obtener segmentos disponibles
     * @param {string} marcaId - ID de la marca
     * @returns {Promise<object>}
     */
    getAvailableSegments: async (marcaId) => {
        const url = `${BASE_URL}/contacts/segments?marcaId=${marcaId}`;
        return await get(url);
    },
    
    /**
     * Obtener análisis RFM
     * @param {string} marcaId - ID de la marca
     * @param {number} periodMonths - Período en meses (default: 12)
     * @returns {Promise<object>}
     */
    getRFMAnalysis: async (marcaId, periodMonths = 12) => {
        return await get(`${BASE_URL}/contacts/rfm-analysis?marcaId=${marcaId}&periodMonths=${periodMonths}`);
    },
    
    // ===== CAMPAÑAS =====
    
    /**
     * Listar campañas
     * @param {string} marcaId - ID de la marca
     * @param {object} filters - Filtros opcionales (page, limit, status, search, includeStats)
     * @returns {Promise<object>}
     */
    getCampaigns: async (marcaId, filters = {}) => {
        const params = new URLSearchParams({ marcaId });
        
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, value);
            }
        });
        
        return await get(`${BASE_URL}/campaigns?${params.toString()}`);
    },
    
    /**
     * Obtener campaña individual
     * @param {string} id - ID de la campaña
     * @returns {Promise<object>}
     */
    getCampaign: async (id) => {
        return await get(`${BASE_URL}/campaigns/${id}`);
    },
    
    /**
     * Crear campaña
     * @param {string} marcaId - ID de la marca
     * @param {object} campaignData - Datos de la campaña
     * @returns {Promise<object>}
     */
    createCampaign: async (marcaId, campaignData) => {
        const url = `${BASE_URL}/campaigns?marcaId=${marcaId}`;
        return await post(url, campaignData);
    },
    
    /**
     * Actualizar campaña (solo si está en draft)
     * @param {string} id - ID de la campaña
     * @param {object} updates - Campos a actualizar
     * @returns {Promise<object>}
     */
    updateCampaign: async (id, updates) => {
        return await patch(`${BASE_URL}/campaigns/${id}`, updates);
    },
    
    /**
     * Eliminar campaña
     * @param {string} id - ID de la campaña
     * @returns {Promise<object>}
     */
    deleteCampaign: async (id) => {
        return await del(`${BASE_URL}/campaigns/${id}`);
    },
    
    /**
     * Calcular destinatarios de una campaña existente
     * @param {string} id - ID de la campaña
     * @returns {Promise<object>}
     */
    calculateRecipients: async (id) => {
        return await get(`${BASE_URL}/campaigns/${id}/recipients-count`);
    },
    
    /**
     * Calcular destinatarios ANTES de crear una campaña (usando filtros de segmentación)
     * Este método usa el mismo endpoint de contactos que el backend usa internamente
     * para calcular destinatarios, asegurando que el número sea exacto
     * @param {string} marcaId - ID de la marca
     * @param {object} segmentFilters - Filtros de segmentación
     * @returns {Promise<number>} - Número exacto de destinatarios
     */
    calculateRecipientsBeforeCreate: async (marcaId, segmentFilters = {}) => {
        // Usar el mismo endpoint de contactos que el backend usa para calcular destinatarios
        const filters = {
            acceptsMarketing: true,
            limit: 1 // Solo necesitamos el total, no los datos
        };
        
        // Aplicar los mismos filtros que el backend aplicaría
        if (segmentFilters.rfmSegments?.length > 0) {
            filters.rfmSegments = segmentFilters.rfmSegments;
        }
        if (segmentFilters.productSegments?.length > 0) {
            filters.productSegments = segmentFilters.productSegments;
        }
        if (segmentFilters.behavioralSegments?.length > 0) {
            filters.behavioralSegments = segmentFilters.behavioralSegments;
        }
        if (segmentFilters.tags?.length > 0) {
            filters.tags = segmentFilters.tags;
        }
        if (segmentFilters.country) {
            filters.country = segmentFilters.country;
        }
        if (segmentFilters.city) {
            filters.city = segmentFilters.city;
        }
        if (segmentFilters.lastActiveDays) {
            filters.lastActiveDays = typeof segmentFilters.lastActiveDays === 'string' 
                ? parseInt(segmentFilters.lastActiveDays) 
                : segmentFilters.lastActiveDays;
        }
        
        const response = await emailMarketingService.getContacts(marcaId, filters);
        
        // El backend devuelve el total en pagination.total cuando usamos limit: 1
        const count = response.pagination?.total || response.data?.length || (Array.isArray(response) ? response.length : 0);
        
        return count;
    },
    
    /**
     * Preparar campaña para envío (crea recipients y cambia estado a sending)
     * @param {string} id - ID de la campaña
     * @returns {Promise<object>}
     */
    prepareCampaign: async (id) => {
        return await post(`${BASE_URL}/campaigns/${id}/send`);
    },
    
    /**
     * Procesar campaña (enviar emails en batch)
     * @param {string} id - ID de la campaña
     * @param {number} maxEmails - Máximo de emails a procesar (default: 50)
     * @returns {Promise<object>}
     */
    processCampaign: async (id, maxEmails = 50) => {
        return await post(`${BASE_URL}/campaigns/${id}/process?maxEmails=${maxEmails}`);
    },
    
    /**
     * Obtener estadísticas completas de una campaña
     * @param {string} id - ID de la campaña
     * @returns {Promise<object>}
     */
    getCampaignStats: async (id) => {
        return await get(`${BASE_URL}/campaigns/${id}/stats`);
    },
    
    /**
     * Obtener estadísticas de engagement de una campaña
     * @param {string} campaignId - ID de la campaña
     * @param {string} marcaId - ID de la marca
     * @returns {Promise<object>} - Estadísticas de engagement (opened, clicked, rates)
     */
    getEngagementStats: async (campaignId, marcaId) => {
        const url = `${BASE_URL}/campaigns/${campaignId}/engagement?marcaId=${marcaId}`;
        return await get(url);
    },
    
    /**
     * Reenviar campaña a recipients que no abrieron el email
     * @param {string} campaignId - ID de la campaña
     * @param {string} marcaId - ID de la marca
     * @param {object} options - Opciones de reenvío
     * @param {number} options.delayDays - Días de espera antes de reenviar (default: 3)
     * @param {string} options.newSubject - Nuevo asunto para el reenvío (opcional)
     * @returns {Promise<object>} - Resultado del reenvío (totalRecipients, resentCount, skippedCount)
     */
    resendToNonOpeners: async (campaignId, marcaId, options = {}) => {
        const params = new URLSearchParams({ marcaId });
        
        if (options.delayDays !== undefined) {
            params.append('delayDays', options.delayDays.toString());
        }
        
        if (options.newSubject) {
            params.append('newSubject', options.newSubject);
        }
        
        const url = `${BASE_URL}/campaigns/${campaignId}/resend?${params.toString()}`;
        return await post(url, {});
    },
    
    // ===== UNSUBSCRIBE (Público - Sin autenticación) =====
    
    /**
     * Procesar baja (GET) - Página pública
     * @param {string} token - Token de unsubscribe
     * @returns {Promise<string>} - HTML de la página
     */
    unsubscribe: async (token) => {
        return await get(`${BASE_URL}/unsubscribe/${token}`);
    },
    
    /**
     * Confirmar baja (POST) - Página pública
     * @param {string} token - Token de unsubscribe
     * @returns {Promise<string>} - HTML de confirmación
     */
    confirmUnsubscribe: async (token) => {
        return await post(`${BASE_URL}/unsubscribe/${token}`, { confirm: true });
    },
    
    // ===== DELIVERABILITY & WARM-UP =====
    
    /**
     * Obtener estado de warm-up de una marca
     * @param {string} marcaId - ID de la marca
     * @returns {Promise<object>} - Estado del warm-up
     */
    getWarmupStatus: async (marcaId) => {
        return await get(`mail/warmup/status?marcaId=${marcaId}`);
    }
};

export default emailMarketingService;

